import { ReactNode, useEffect } from 'react';

import { Modal } from 'common/modal/modal';
import { classnames } from 'helpers/utils';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Spinner } from 'common/spinner';
import { captureEvent } from 'posthog';
import CypressIds from 'cypressIds';
import styles from './modal-warning.module.scss';

export type ModalWarningIconVariant = 'bordered' | 'danger';

type WarningModalProps = {
  title?: string;
  icon?: ReactNode;
  iconVariant?: ModalWarningIconVariant;
  content?: ReactNode;
  className?: string;
  contentClassName?: string;
  showCancelButton?: boolean;
  disableManualClosing?: boolean;
  closeButtonText?: string;
  closeStyleButton?: ButtonStyle;
  successButtonText?: string;
  successStyleButton?: ButtonStyle;
  successButtonSize?: ButtonSize;
  loading?: boolean;
  cancelActionFn?: () => void;
  closeFn?: () => void;
  successFn?: () => void;
  disableSuccessButton?: boolean;
};

const ModalWarning = ({
  title = '',
  icon,
  iconVariant,
  content,
  className = '',
  contentClassName = '',
  showCancelButton = true,
  disableManualClosing = false,
  closeButtonText = 'Cancel',
  successButtonText = 'Accept',
  closeStyleButton = ButtonStyle.GreyGhost,
  successStyleButton = ButtonStyle.SecondaryFilled,
  successButtonSize = ButtonSize.Medium,
  loading = false,
  cancelActionFn,
  closeFn,
  successFn = () => {},
  disableSuccessButton = false,
}: WarningModalProps) => {
  const closeOnEscapeKeyDown = (e: any) => {
    if ((e.charCode || e.keyCode) === 27) {
      closeFn!();
    }
  };

  useEffect(() => {
    document.body.addEventListener('keydown', closeOnEscapeKeyDown);
    return function cleanup() {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, []);

  const cancelButton = () => (
    <Button
      buttonStyle={closeStyleButton}
      className={styles.cancelButton}
      buttonSize={successButtonSize}
      onClick={closeFn}
    >
      {closeButtonText}
    </Button>
  );

  const successButton = () => (
    <Button
      dataCy={CypressIds.confirmModalButton}
      buttonStyle={successStyleButton}
      className={styles.successButton}
      buttonSize={successButtonSize}
      onClick={() => {
        captureEvent('modalConfirmButtonClicked', {
          modalTitle: title,
          buttonText: successButtonText,
        });
        successFn();
      }}
      disabled={disableSuccessButton}
    >
      {successButtonText}
    </Button>
  );

  const footerButtons = () => (
    <>
      {showCancelButton && cancelButton()}
      {successButton()}
    </>
  );

  const getIconWrapperClassName = () => {
    if (iconVariant === 'bordered') {
      return classnames(styles.iconWrapper, styles.iconWrapperBordered);
    }
    if (iconVariant === 'danger') {
      return classnames(styles.iconWrapper, styles.iconWrapperDanger);
    }
    return styles.iconWrapper;
  };

  const modalTitle =
    icon != null ? (
      <div className={styles.headerTitleWithIcon}>
        <div className={getIconWrapperClassName()}>{icon}</div>
        {title ? (
          <h2 className="text__body__semi__bold__large__gray900">{title}</h2>
        ) : null}
      </div>
    ) : (
      title
    );

  const showModal = () => (
    <Modal
      title={modalTitle}
      onClose={cancelActionFn || closeFn}
      disableManualClosing={disableManualClosing}
      className={classnames(styles.modal, className)}
      contentClassName={classnames(styles.contentModal, contentClassName)}
    >
      <div
        className={classnames(
          'text__body__regular__medium__gray800',
          styles.content,
        )}
      >
        {content}
      </div>
      <div className={styles.buttonsfooter}>
        {loading ? (
          <Spinner
            className={classnames(
              globalStyles.smallSpinner,
              styles.spinnerButtons,
            )}
          />
        ) : (
          footerButtons()
        )}
      </div>
    </Modal>
  );

  return showModal();
};

export { ModalWarning };
